import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {buildPdfFileName, downloadBlobAsFile} from '../utils/fileDownload'
import {buildReportProgressToastMessage} from '../components/ReportProgressToastMessage'
import {useReportGenerationToasts} from './ReportGenerationToastContext'

export type ReportGenerationJobAction = 'preview' | 'generate'

export type ReportGenerationJobStatus =
  | 'queued'
  | 'inprogress'
  | 'completed'
  | 'failed'
  | 'expired'

export interface ReportGenerationJob {
  jobId: string
  module: string
  resourceId: string
  action: ReportGenerationJobAction
  status: ReportGenerationJobStatus
  progressPercent: number
  stage: string
  errorMessage?: string
  fileName?: string
  contentType?: string
  fileSize?: number
  startedAt?: string
  completedAt?: string
  expiresAt?: string
}

export type ReportGenerationJobAdapter = {
  module: string
  title: string
  previewTitle: string
  getJob: (jobId: string) => Promise<ReportGenerationJob>
  getActiveJob: (resourceId: string) => Promise<ReportGenerationJob | undefined>
  downloadFile: (jobId: string) => Promise<Blob>
}

type TrackedReportGenerationJob = {
  jobId: string
  module: string
  resourceId: string
  action: ReportGenerationJobAction
  processId: string
  fallbackFileName: string
  adapter: ReportGenerationJobAdapter
}

type TrackReportGenerationJobOptions = {
  processId?: string
  fallbackFileName?: string
}

type EnsureReportGenerationJobOptions = {
  fallbackFileName?: string
}

interface ReportGenerationJobTrackerContextValue {
  trackReportJob: (job: ReportGenerationJob, adapter: ReportGenerationJobAdapter, options?: TrackReportGenerationJobOptions) => string
  ensureTrackingForResource: (resourceId: string, adapter: ReportGenerationJobAdapter, options?: EnsureReportGenerationJobOptions) => Promise<void>
  isReportJobActive: (module: string, resourceId?: string) => boolean
  getReportJobAction: (module: string, resourceId?: string) => ReportGenerationJobAction | null
}

const ReportGenerationJobTrackerContext =
  createContext<ReportGenerationJobTrackerContextValue | undefined>(undefined)

const POLL_INTERVAL_MS = 2000
const PREVIEW_TOAST_EXPIRY_MS = 2 * 60 * 60 * 1000
const PdfPreviewModal = React.lazy(() => import('../components/PdfPreviewModal'))

const removeTrackedJob = (items: Record<string, TrackedReportGenerationJob>, jobId: string) => {
  if (!items[jobId]) {
    return items
  }

  const next = {...items}
  delete next[jobId]
  return next
}

const buildActiveKey = (module: string, resourceId: string) => `${module}:${resourceId}`

export const ReportGenerationJobTrackerProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const {startProcess, updateProcess, completeProcess, failProcess, dismissProcess} = useReportGenerationToasts()
  const [trackedJobs, setTrackedJobs] = useState<Record<string, TrackedReportGenerationJob>>({})
  const trackedJobsRef = useRef<Record<string, TrackedReportGenerationJob>>({})
  const pollInFlightRef = useRef<Set<string>>(new Set())
  const ensureInFlightKeysRef = useRef<Set<string>>(new Set())
  const [previewBlob, setPreviewBlob] = useState<Blob | undefined>()
  const [previewFileName, setPreviewFileName] = useState('report.pdf')
  const [previewTitle, setPreviewTitle] = useState('Report Preview')
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  useEffect(() => {
    trackedJobsRef.current = trackedJobs
  }, [trackedJobs])

  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false)
    setPreviewBlob(undefined)
  }, [])

  const openPreviewFromJob = useCallback(
    async (tracked: TrackedReportGenerationJob, fileName: string) => {
      try {
        const blob = await tracked.adapter.downloadFile(tracked.jobId)
        setPreviewBlob(blob)
        setPreviewFileName(buildPdfFileName(fileName))
        setPreviewTitle(tracked.adapter.previewTitle)
        setShowPreviewModal(true)
        dismissProcess(tracked.processId)
      } catch (error) {
        console.error('Failed to open completed report preview job', error)
        failProcess(tracked.processId, {
          title: tracked.adapter.title,
          message: 'Preview is ready, but opening the PDF failed. Please generate again.',
        })
      }
    },
    [dismissProcess, failProcess]
  )

  const trackReportJob = useCallback(
    (job: ReportGenerationJob, adapter: ReportGenerationJobAdapter, options?: TrackReportGenerationJobOptions) => {
      const existing = trackedJobsRef.current[job.jobId]
      if (existing) {
        if (options?.processId && options.processId !== existing.processId) {
          dismissProcess(options.processId)
        }
        return existing.processId
      }

      const resourceId = String(job.resourceId)
      const processId = options?.processId ?? startProcess({
        title: adapter.title,
        message: buildReportProgressToastMessage(job.progressPercent ?? 0, job.stage),
      })
      const fallbackFileName =
        options?.fallbackFileName?.trim() ||
        job.fileName?.trim() ||
        `${adapter.module}-${resourceId}.pdf`
      const action: ReportGenerationJobAction = job.action === 'preview' ? 'preview' : 'generate'

      setTrackedJobs((current) => ({
        ...current,
        [job.jobId]: {
          jobId: job.jobId,
          module: adapter.module,
          resourceId,
          action,
          processId,
          fallbackFileName,
          adapter,
        },
      }))

      return processId
    },
    [dismissProcess, startProcess]
  )

  const handleCompletedJob = useCallback(
    async (tracked: TrackedReportGenerationJob, job: ReportGenerationJob) => {
      const resolvedFileName = job.fileName?.trim() || tracked.fallbackFileName || 'report.pdf'

      if (tracked.action === 'preview') {
        completeProcess(tracked.processId, {
          title: tracked.adapter.title,
          message: 'Preview is ready.',
          autoDismissMs: PREVIEW_TOAST_EXPIRY_MS,
          action: {
            label: 'Open Preview',
            onClick: () => {
              void openPreviewFromJob(tracked, resolvedFileName)
            },
          },
        })
        return
      }

      try {
        const blob = await tracked.adapter.downloadFile(job.jobId)
        downloadBlobAsFile(blob, resolvedFileName)
        completeProcess(tracked.processId, {
          title: tracked.adapter.title,
          message: 'Report generated. Your PDF download has started.',
        })
      } catch (error) {
        console.error('Failed to download completed report job file', error)
        failProcess(tracked.processId, {
          title: tracked.adapter.title,
          message: 'Report generation completed, but downloading the PDF failed. Please try again.',
        })
      }
    },
    [completeProcess, failProcess, openPreviewFromJob]
  )

  const pollTrackedJob = useCallback(
    async (tracked: TrackedReportGenerationJob) => {
      if (pollInFlightRef.current.has(tracked.jobId)) {
        return
      }

      pollInFlightRef.current.add(tracked.jobId)
      try {
        const job = await tracked.adapter.getJob(tracked.jobId)
        updateProcess(tracked.processId, {
          title: tracked.adapter.title,
          message: buildReportProgressToastMessage(job.progressPercent ?? 0, job.stage),
        })

        if (job.status === 'completed') {
          await handleCompletedJob(tracked, job)
          setTrackedJobs((current) => removeTrackedJob(current, tracked.jobId))
          return
        }

        if (job.status === 'failed' || job.status === 'expired') {
          failProcess(tracked.processId, {
            title: tracked.adapter.title,
            message: job.errorMessage?.trim() || 'Unable to generate the report right now.',
          })
          setTrackedJobs((current) => removeTrackedJob(current, tracked.jobId))
        }
      } catch (error) {
        console.error('Failed to poll report generation job', error)
        failProcess(tracked.processId, {
          title: tracked.adapter.title,
          message: 'Unable to check report generation progress right now.',
        })
        setTrackedJobs((current) => removeTrackedJob(current, tracked.jobId))
      } finally {
        pollInFlightRef.current.delete(tracked.jobId)
      }
    },
    [failProcess, handleCompletedJob, updateProcess]
  )

  const trackedCount = Object.keys(trackedJobs).length
  useEffect(() => {
    if (trackedCount === 0) {
      return
    }

    const runPoll = () => {
      const snapshot = Object.values(trackedJobsRef.current)
      snapshot.forEach((tracked) => {
        void pollTrackedJob(tracked)
      })
    }

    runPoll()
    const timeoutId = window.setInterval(runPoll, POLL_INTERVAL_MS)
    return () => {
      window.clearInterval(timeoutId)
    }
  }, [pollTrackedJob, trackedCount])

  const ensureTrackingForResource = useCallback(
    async (resourceId: string, adapter: ReportGenerationJobAdapter, options?: EnsureReportGenerationJobOptions) => {
      const normalizedResourceId = String(resourceId).trim()
      if (!normalizedResourceId) {
        return
      }

      if (
        Object.values(trackedJobsRef.current).some(
          (tracked) => tracked.module === adapter.module && tracked.resourceId === normalizedResourceId
        )
      ) {
        return
      }

      const activeKey = buildActiveKey(adapter.module, normalizedResourceId)
      if (ensureInFlightKeysRef.current.has(activeKey)) {
        return
      }

      ensureInFlightKeysRef.current.add(activeKey)
      try {
        const activeJob = await adapter.getActiveJob(normalizedResourceId)
        if (!activeJob?.jobId) {
          return
        }

        trackReportJob(activeJob, adapter, {
          fallbackFileName:
            options?.fallbackFileName?.trim() ||
            `${adapter.module}-${normalizedResourceId}.pdf`,
        })
      } catch (error) {
        console.error('Failed to resume active report generation job', error)
      } finally {
        ensureInFlightKeysRef.current.delete(activeKey)
      }
    },
    [trackReportJob]
  )

  const activeActionByKey = useMemo(() => {
    const map: Record<string, ReportGenerationJobAction> = {}
    Object.values(trackedJobs).forEach((tracked) => {
      map[buildActiveKey(tracked.module, tracked.resourceId)] = tracked.action
    })
    return map
  }, [trackedJobs])

  const isReportJobActive = useCallback(
    (module: string, resourceId?: string) => {
      if (!resourceId) {
        return false
      }
      return Boolean(activeActionByKey[buildActiveKey(module, String(resourceId))])
    },
    [activeActionByKey]
  )

  const getReportJobAction = useCallback(
    (module: string, resourceId?: string) => {
      if (!resourceId) {
        return null
      }
      return activeActionByKey[buildActiveKey(module, String(resourceId))] ?? null
    },
    [activeActionByKey]
  )

  const value = useMemo<ReportGenerationJobTrackerContextValue>(
    () => ({
      trackReportJob,
      ensureTrackingForResource,
      isReportJobActive,
      getReportJobAction,
    }),
    [ensureTrackingForResource, getReportJobAction, isReportJobActive, trackReportJob]
  )

  return (
    <ReportGenerationJobTrackerContext.Provider value={value}>
      {children}
      {showPreviewModal ? (
        <React.Suspense fallback={null}>
          <PdfPreviewModal
            show={showPreviewModal}
            blob={previewBlob}
            title={previewTitle}
            fileName={previewFileName}
            onClose={closePreviewModal}
          />
        </React.Suspense>
      ) : null}
    </ReportGenerationJobTrackerContext.Provider>
  )
}

export const useReportGenerationJobTracker = () => {
  const context = useContext(ReportGenerationJobTrackerContext)
  if (!context) {
    throw new Error('useReportGenerationJobTracker must be used within ReportGenerationJobTrackerProvider')
  }
  return context
}
